import React, { useState, useCallback, useRef, ReactNode } from 'react';
import Cropper, { Point, Area } from 'react-easy-crop';
import Pica from 'pica';
import { useAdminUploadFile } from '@/hooks/useAdminUploadFile';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { createLogger } from '@/utils/createLogger';
import { outputMimeFor } from '@/utils/outputImageMime';

const logger = createLogger('CropAndUploadDialog');

interface CropAndUploadDialogProps {
  children: ReactNode;
  ratio?: number;
  onSave: (url: string) => void;
  maxWidth?: number;
  maxHeight?: number;
}

const pica = new Pica();

const createImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const canvasToFile = (
  canvas: HTMLCanvasElement,
  mimeType: 'image/jpeg' | 'image/png',
  filename: string,
): Promise<File> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(new File([blob], filename, { type: mimeType }));
        } else {
          reject(new Error('Failed to encode cropped image'));
        }
      },
      mimeType,
      mimeType === 'image/jpeg' ? 0.85 : undefined,
    );
  });

const getCroppedImg = async (
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  maxWidth?: number,
  maxHeight?: number,
  sourceType?: string,
): Promise<File> => {
  const { mimeType, filename } = outputMimeFor(sourceType);
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d', { alpha: true });

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  // Calculate rotated dimensions
  const rotRad = (rotation * Math.PI) / 180;
  const { width: bBoxWidth, height: bBoxHeight } = getBoundingBox(
    image.width,
    image.height,
    rotation,
  );

  // Set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // Translate to center, rotate, then translate back
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.translate(-image.width / 2, -image.height / 2);

  // Draw rotated image
  ctx.drawImage(image, 0, 0);

  // Create cropped canvas
  const croppedCanvas = document.createElement('canvas');
  const croppedCtx = croppedCanvas.getContext('2d', { alpha: true });

  if (!croppedCtx) {
    throw new Error('Could not get cropped canvas context');
  }

  // Set size to crop dimensions
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped portion
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // Calculate final dimensions for downscaling
  let finalWidth = pixelCrop.width;
  let finalHeight = pixelCrop.height;

  if (maxWidth && finalWidth > maxWidth) {
    finalHeight = (finalHeight * maxWidth) / finalWidth;
    finalWidth = maxWidth;
  }

  if (maxHeight && finalHeight > maxHeight) {
    finalWidth = (finalWidth * maxHeight) / finalHeight;
    finalHeight = maxHeight;
  }

  // Use pica to resize if needed
  if (finalWidth !== pixelCrop.width || finalHeight !== pixelCrop.height) {
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = finalWidth;
    resizedCanvas.height = finalHeight;

    await pica.resize(croppedCanvas, resizedCanvas, {
      quality: 3,
    });

    return canvasToFile(resizedCanvas, mimeType, filename);
  }

  return canvasToFile(croppedCanvas, mimeType, filename);
};

const getBoundingBox = (width: number, height: number, rotation: number) => {
  const rotRad = (rotation * Math.PI) / 180;
  const rotatedWidth =
    Math.abs(width * Math.cos(rotRad)) + Math.abs(height * Math.sin(rotRad));
  const rotatedHeight =
    Math.abs(width * Math.sin(rotRad)) + Math.abs(height * Math.cos(rotRad));
  return { width: rotatedWidth, height: rotatedHeight };
};

export function CropAndUploadDialog({
  children,
  ratio,
  onSave,
  maxWidth,
  maxHeight,
}: CropAndUploadDialogProps) {
  const [open, setOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [sourceType, setSourceType] = useState<string | undefined>();
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [adminUploadFile, { loading }] = useAdminUploadFile();

  const onCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleImageChange = (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setSourceType(file.type);
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageChange(file);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) {
      handleImageChange(file);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handlePaste = async () => {
    try {
      const clipboardItems = await navigator.clipboard.read();
      for (const item of clipboardItems) {
        const imageType = item.types.find((type) => type.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          const file = new File([blob], 'pasted-image.png', {
            type: imageType,
          });
          handleImageChange(file);
          return;
        }
      }
    } catch (err) {
      logger.error('Failed to paste from clipboard', err);
    }
  };

  const handleAccept = async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    try {
      const croppedFile = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        rotation,
        maxWidth,
        maxHeight,
        sourceType,
      );

      const url = await adminUploadFile(croppedFile);
      onSave(url);
      handleClose();
    } catch (error) {
      logger.error('Error processing image:', error);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setImageSrc(null);
    setSourceType(undefined);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setIsDragOver(false);
  };

  const handleClearImage = () => {
    setImageSrc(null);
    setSourceType(undefined);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {imageSrc ? 'Crop Image' : 'Select Image'}
            </DialogTitle>
          </DialogHeader>

          {!imageSrc ? (
            // Image Selection Stage
            <div className="flex flex-col gap-4">
              <div
                className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                  isDragOver
                    ? 'border-primary bg-primary/10'
                    : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <div className="space-y-4">
                  <div className="text-6xl">🖼️</div>
                  <div>
                    <p className="text-lg font-medium">Drop an image here</p>
                    <p className="text-muted-foreground">
                      or click to browse files
                    </p>
                  </div>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                  >
                    Choose File
                  </Button>
                </div>
              </div>

              <div className="text-center">
                <Button onClick={handlePaste} variant="outline">
                  📋 Paste from Clipboard
                </Button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          ) : (
            // Image Cropping Stage
            <div className="flex h-[500px] flex-col gap-4">
              <div className="relative flex-1 overflow-hidden rounded-lg bg-black">
                <Cropper
                  image={imageSrc}
                  crop={crop}
                  zoom={zoom}
                  aspect={ratio || 1}
                  rotation={rotation}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onRotationChange={setRotation}
                  onCropComplete={onCropComplete}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="w-16 text-sm font-medium">Zoom:</label>
                  <Slider
                    value={[zoom]}
                    onValueChange={(value: number[]) => setZoom(value[0])}
                    min={1}
                    max={3}
                    step={0.1}
                    className="flex-1"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <label className="w-16 text-sm font-medium">Rotation:</label>
                  <Slider
                    value={[rotation]}
                    onValueChange={(value: number[]) => setRotation(value[0])}
                    min={0}
                    max={360}
                    step={1}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {imageSrc ? (
              <div className="flex gap-2">
                <Button
                  onClick={handleClearImage}
                  variant="outline"
                  disabled={loading}
                >
                  Clear Image
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outline"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAccept}
                  disabled={loading || !croppedAreaPixels}
                >
                  {loading ? 'Uploading...' : 'Accept & Upload'}
                </Button>
              </div>
            ) : (
              <Button onClick={handleClose} variant="outline">
                Cancel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
